import { useEffect, useMemo, useState } from "react";
import { Box, Grid, Pagination, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks";
import { fetchAlliances } from "../store/slices/alliancesSlice";
import { PAGE_SIZE_ALLIANCES } from "../constants";
import { AllianceCard } from "../components/AllianceCard";
import { SearchFilter } from "../components/SearchFilter";
import { PageHeader } from "../components/PageHeader";
import { LoadingSpinner } from "../components/LoadingSpinner";
import {
  alliancesSortValue,
  buildAlliancesSearchParams,
  parseAlliancesSearchParams,
  parseAlliancesSortValue,
} from "../utils/alliancesSearchParams";

export function AlliancesPage() {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, total, page, totalPages, loading } = useAppSelector(
    (s) => s.alliances,
  );

  const applied = useMemo(
    () => parseAlliancesSearchParams(searchParams),
    [searchParams],
  );
  const [searchDraft, setSearchDraft] = useState(applied.search);

  useEffect(() => {
    setSearchDraft(applied.search);
  }, [applied.search]);

  const listQuery = useMemo(() => {
    const { sortBy, sortOrder, search, page: pageNum } = applied;
    return {
      sortBy,
      sortOrder,
      page: pageNum,
      limit: PAGE_SIZE_ALLIANCES,
      ...(search ? { search } : {}),
    };
  }, [applied]);

  useEffect(() => {
    dispatch(fetchAlliances(listQuery));
  }, [dispatch, listQuery]);

  const updateApplied = (
    patch: Partial<typeof applied>,
    options?: { replace?: boolean },
  ) => {
    const next = { ...applied, ...patch };
    setSearchParams(buildAlliancesSearchParams(next), {
      replace: options?.replace,
    });
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    updateApplied({ page: newPage });
  };

  const sortValue = alliancesSortValue(applied.sortBy, applied.sortOrder);

  return (
    <Box>
      <PageHeader
        title="Pretraga"
        subtitle="Središnji registar i pregled svih nacionalnih sportskih saveza Republike Hrvatske. Pregledajte statistike, dokumentaciju i kontakte."
      />

      <SearchFilter
        search={searchDraft}
        sortValue={sortValue}
        onSearchChange={setSearchDraft}
        onApply={() =>
          updateApplied({
            search: searchDraft.trim(),
            page: 1,
          })
        }
        onSortChange={(nextSort) => {
          const { sortBy, sortOrder } = parseAlliancesSortValue(nextSort);
          updateApplied({ sortBy, sortOrder, page: 1 });
        }}
        onReset={() => {
          setSearchDraft("");
          setSearchParams(new URLSearchParams(), { replace: true });
        }}
      />

      {loading ? (
        <LoadingSpinner py={6} />
      ) : (
        <>
          <Grid container spacing={{ xs: 2.5, sm: 3 }}>
            {items.map((alliance: any) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={alliance._id}>
                <AllianceCard alliance={alliance} />
              </Grid>
            ))}
          </Grid>

          {items.length === 0 && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography color="text.secondary">
                Nema pronađenih saveza za zadane kriterije.
              </Typography>
            </Box>
          )}

          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: { xs: 2, sm: 0 },
                mt: 4,
                pt: 3,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: { xs: "center", sm: "left" } }}
              >
                Prikazano {(page - 1) * PAGE_SIZE_ALLIANCES + 1}-
                {Math.min(page * PAGE_SIZE_ALLIANCES, total)} od {total} saveza
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="secondary"
                size="small"
                siblingCount={0}
                boundaryCount={1}
                sx={{
                  alignSelf: { xs: "center", sm: "auto" },
                  "& .MuiPagination-ul": {
                    flexWrap: "wrap",
                    justifyContent: "center",
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
